/**
 * A labelled text input in the same voice as `ChipGroup` — same label
 * treatment, same sizing, so a typed field does not look like a different
 * app from the rows of chips above it.
 */

import { useId, type ReactNode } from "react";

interface TextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  maxLength?: number;
  hint?: string;
  /** Shown in place of the counter when the value needs flagging. */
  note?: string;
  /** Rendered beside the input, inside the same row — a compact control tied
   *  to this field (e.g. an icon picker), not a second field of its own. An
   *  explicit `htmlFor`/`id` pair rather than wrapping everything in
   *  `<label>`, so a leading button's own click does not also try to focus
   *  the input via the browser's implicit label-forwarding. */
  leading?: ReactNode;
}

export function TextField({
  label,
  value,
  onChange,
  placeholder,
  maxLength,
  hint,
  note,
  leading,
}: TextFieldProps) {
  const id = useId();
  return (
    <div>
      <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <label
          htmlFor={id}
          className="font-display text-[13px] font-semibold uppercase tracking-[0.16em] text-graphite-2"
        >
          {label}
        </label>
        {note ? (
          <span className="font-display text-[11px] font-semibold uppercase tracking-[0.05em] text-smb">
            {note}
          </span>
        ) : (
          maxLength && (
            <span className="font-mono text-[11px] text-graphite-2">
              {value.length}/{maxLength}
            </span>
          )
        )}
      </div>
      <div className="flex gap-[7px]">
        {leading}
        <input
          id={id}
          type="text"
          value={value}
          placeholder={placeholder}
          maxLength={maxLength}
          onChange={(e) => onChange(e.target.value)}
          // Not `uppercase`, unlike the chips beside it: those render fixed
          // labels, this renders what the diver typed. A CSS-only upcase would
          // show "DIVE WRECK" while the payload carried "dive wreck", and for
          // this field that difference decides whether the camera arms $DIVE.
          className="w-full min-w-0 flex-1 rounded-[2px] border-[1.5px] border-graphite bg-field px-[11px] py-[5px] font-mono text-[14px] font-semibold text-graphite placeholder:font-body placeholder:font-normal placeholder:text-graphite-2/70"
        />
      </div>
      {hint && (
        <span className="mt-1 block text-[11.5px] leading-snug text-graphite-2">{hint}</span>
      )}
    </div>
  );
}
